import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { AsyncPipe } from "@angular/common";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { Observable, of } from "rxjs";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    map,
    startWith,
    switchMap,
    tap,
} from "rxjs/operators";
import { OverlayPanel, OverlayPanelModule } from "primeng/overlaypanel";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from "primeng/tooltip";
import { TermPipe } from "@/_pipes/term.pipe";
import { GlobalSearchService } from "@/_services/global-search.service";
import {
    GlobalSearchResultRow,
    GlobalSearchResultSection,
    RESULTS_LIST_ID,
    allRows,
    toResultSections,
} from "./global-search-record-types";

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 250;
const NO_HIGHLIGHT = -1;

type GlobalSearchStatus =
    | "idle"
    | "searching"
    | "found"
    | "noMatches"
    | "failed";

interface GlobalSearchState {
    query: string;
    status: GlobalSearchStatus;
    sections: GlobalSearchResultSection[];
}

function stateOf(
    query: string,
    status: GlobalSearchStatus,
    sections: GlobalSearchResultSection[] = [],
): GlobalSearchState {
    return { query, status, sections };
}

function resultsStateFor(
    query: string,
    sections: GlobalSearchResultSection[],
): GlobalSearchState {
    return stateOf(query, sections.length ? "found" : "noMatches", sections);
}

@Component({
    selector: "app-global-search",
    standalone: true,
    imports: [
        AsyncPipe,
        ReactiveFormsModule,
        RouterLink,
        OverlayPanelModule,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        TooltipModule,
        TermPipe,
    ],
    templateUrl: "./global-search.component.html",
    styleUrls: ["./global-search.component.scss"],
})
export class GlobalSearchComponent implements OnInit {
    @Input() collapsed = false;

    @ViewChild("resultsPanel") resultsPanel: OverlayPanel;
    @ViewChild("panelInput") panelInput: ElementRef<HTMLInputElement>;
    @ViewChild("navInput") navInput: ElementRef<HTMLInputElement>;
    @ViewChild("searchButton") searchButton: ElementRef<HTMLButtonElement>;

    readonly searchControl = new FormControl("", { nonNullable: true });
    readonly resultsListId = RESULTS_LIST_ID;
    state$: Observable<GlobalSearchState>;
    isPanelOpen = false;
    highlightedPosition = NO_HIGHLIGHT;
    highlightedOptionId: string | null = null;
    controlledListId: string | null = null;
    private rows: GlobalSearchResultRow[] = [];
    private rowsQuery = "";

    constructor(
        private readonly globalSearchService: GlobalSearchService,
        private readonly router: Router,
    ) {}

    ngOnInit(): void {
        this.state$ = this.searchControl.valueChanges.pipe(
            map((value) => value.trim()),
            debounceTime(SEARCH_DEBOUNCE_MS),
            distinctUntilChanged(),
            switchMap((query) => this.searchStatesFor(query)),
            startWith(stateOf("", "idle")),
            tap((state) => {
                this.rows = allRows(state.sections);
                this.rowsQuery = state.query;
                this.controlledListId = this.rows.length
                    ? RESULTS_LIST_ID
                    : null;
                this.clearHighlight();
            }),
        );
    }

    onType(event: Event, target: HTMLElement): void {
        this.clearHighlight();
        this.showResults(event, target);
    }

    showResults(event: Event, target: HTMLElement): void {
        if (!this.resultsPanel.overlayVisible) {
            this.resultsPanel.show(event, target);
        }
    }

    focusPanelInput(): void {
        this.panelInput?.nativeElement.focus();
    }

    setPanelOpen(isOpen: boolean): void {
        this.isPanelOpen = isOpen;
        this.clearHighlight();
    }

    moveHighlight(step: number, event: Event, searchBox: HTMLElement): void {
        event.preventDefault();
        if (!this.resultsPanel.overlayVisible) {
            if (step > 0) {
                this.showResults(event, searchBox);
            }
            return;
        }
        if (!this.hasCurrentResults()) {
            return;
        }
        this.highlight(this.clampedPosition(this.highlightedPosition + step));
        this.highlightedRowElement()?.scrollIntoView({ block: "nearest" });
    }

    tabIntoResults(event: Event): void {
        if (this.showsCurrentResults()) {
            event.preventDefault();
            this.focusRow(0);
        }
    }

    moveFocus(step: number, position: number, event: Event): void {
        event.preventDefault();
        if (position + step < 0) {
            this.returnToSearchBox(event);
            return;
        }
        this.focusRow(this.clampedPosition(position + step));
    }

    tabFromRow(event: Event, position: number): void {
        if (position === this.rows.length - 1) {
            this.leaveResults(event);
        }
    }

    shiftTabFromRow(event: Event, position: number): void {
        if (position === 0) {
            this.returnToSearchBox(event);
        }
    }

    leaveResults(event: Event): void {
        event.preventDefault();
        this.resultsPanel.hide();
        this.focusTargetAfterResults()?.focus();
    }

    highlight(position: number): void {
        this.highlightedPosition = position;
        this.highlightedOptionId = this.rows[position]?.optionId ?? null;
    }

    openHighlightedResult(): void {
        if (this.showsCurrentResults()) {
            this.router.navigate(
                this.rows[Math.max(this.highlightedPosition, 0)].route,
            );
            this.closeSearch();
        }
    }

    closeSearch(): void {
        this.resultsPanel.hide();
        this.searchControl.setValue("");
    }

    private clearHighlight(): void {
        this.highlight(NO_HIGHLIGHT);
    }

    private showsCurrentQuery(): boolean {
        return this.rowsQuery === this.searchControl.value.trim();
    }

    private hasCurrentResults(): boolean {
        return this.showsCurrentQuery() && this.rows.length > 0;
    }

    private showsCurrentResults(): boolean {
        return this.isPanelOpen && this.hasCurrentResults();
    }

    private clampedPosition(position: number): number {
        return Math.min(Math.max(position, 0), this.rows.length - 1);
    }

    private focusRow(position: number): void {
        this.highlight(position);
        this.highlightedRowElement()?.focus();
    }

    private returnToSearchBox(event: Event): void {
        event.preventDefault();
        this.searchBoxElement()?.focus();
    }

    private highlightedRowElement(): HTMLElement | null | undefined {
        return this.resultsPanel.container?.querySelector<HTMLElement>(
            `#${this.highlightedOptionId}`,
        );
    }

    private searchBoxElement(): HTMLElement | undefined {
        return (this.collapsed ? this.panelInput : this.navInput)
            ?.nativeElement;
    }

    private focusTargetAfterResults(): HTMLElement | undefined {
        return (this.collapsed ? this.searchButton : this.navInput)
            ?.nativeElement;
    }

    private searchStatesFor(query: string): Observable<GlobalSearchState> {
        if (query.length < MIN_QUERY_LENGTH) {
            return of(stateOf(query, "idle"));
        }
        return this.globalSearchService.search(query).pipe(
            map((groups) => resultsStateFor(query, toResultSections(groups))),
            catchError(() => of(stateOf(query, "failed"))),
            startWith(stateOf(query, "searching")),
        );
    }
}
